import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import Colors from 'theme/colors';
import {
  LogoAnchor,
  ensureFFmpeg,
  hasSharedArrayBuffer,
  renderLogoOverlay,
  revokeUrl,
} from './ffmpeg';

const WARN_MB = 100;
const WARN_SEC = 120;

const card: any = {
  backgroundColor: Colors.WHITE,
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
};

const label: any = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 8,
  color: Colors.TEXT,
};

const hint: any = {
  fontSize: 13,
  lineHeight: 1.5,
  color: '#555',
  margin: '0 0 12px',
};

const privacy: any = {
  fontSize: 13,
  lineHeight: 1.5,
  color: '#1b5e20',
  background: '#e8f5e9',
  borderRadius: 8,
  padding: '10px 12px',
  marginBottom: 16,
};

const warnBox: any = {
  fontSize: 13,
  lineHeight: 1.5,
  color: '#e65100',
  background: '#fff3e0',
  borderRadius: 8,
  padding: '10px 12px',
  marginBottom: 12,
};

const btnRow: any = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: 8,
  marginBottom: 12,
};

const btnBase: any = {
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
};

const primaryBtn: any = {
  ...btnBase,
  backgroundColor: Colors.MAIN_COLOR,
  color: Colors.WHITE,
};

const secondaryBtn: any = {
  ...btnBase,
  backgroundColor: '#eceff1',
  color: Colors.TEXT,
};

const dangerBtn: any = {
  ...btnBase,
  backgroundColor: '#ffebee',
  color: '#c62828',
};

const previewWrap: any = {
  position: 'relative' as const,
  width: '100%',
  background: '#000',
  borderRadius: 8,
  overflow: 'hidden',
  userSelect: 'none' as const,
};

const logoOverlay: any = {
  position: 'absolute' as const,
  cursor: 'grab',
  boxShadow: '0 0 0 2px rgba(255,255,255,0.7)',
  borderRadius: 2,
};

const ANCHORS: { id: LogoAnchor; label: string }[] = [
  { id: 'tl', label: 'Trên trái' },
  { id: 'tr', label: 'Trên phải' },
  { id: 'bl', label: 'Dưới trái' },
  { id: 'br', label: 'Dưới phải' },
  { id: 'center', label: 'Giữa' },
  { id: 'custom', label: 'Tùy chỉnh (kéo)' },
];

function formatMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

function resolveLogoBox(
  anchor: LogoAnchor,
  scalePct: number,
  marginPx: number,
  xPct: number,
  yPct: number,
  vw: number,
  vh: number,
  logoNatW: number,
  logoNatH: number
) {
  const w = Math.max(8, (vw * scalePct) / 100);
  const h = logoNatW > 0 ? (w * logoNatH) / logoNatW : w;
  let left = marginPx;
  let top = marginPx;
  if (anchor === 'custom') {
    left = (xPct / 100) * vw;
    top = (yPct / 100) * vh;
  } else if (anchor === 'tr') {
    left = vw - w - marginPx;
    top = marginPx;
  } else if (anchor === 'bl') {
    left = marginPx;
    top = vh - h - marginPx;
  } else if (anchor === 'br') {
    left = vw - w - marginPx;
    top = vh - h - marginPx;
  } else if (anchor === 'center') {
    left = (vw - w) / 2;
    top = (vh - h) / 2;
  }
  return { left, top, w, h };
}

const VideoLogoOverlay = () => {
  const videoRef = useRef<any>(null);
  const previewRef = useRef<any>(null);
  const dragRef = useRef<{ active: boolean; startX: number; startY: number; origXPct: number; origYPct: number }>({
    active: false,
    startX: 0,
    startY: 0,
    origXPct: 0,
    origYPct: 0,
  });
  const abortRef = useRef({ aborted: false });

  const [videoFile, setVideoFile] = useState<any>(null);
  const [logoFile, setLogoFile] = useState<any>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [videoMeta, setVideoMeta] = useState({ w: 0, h: 0, dur: 0 });
  const [logoNat, setLogoNat] = useState({ w: 0, h: 0 });
  const [previewSize, setPreviewSize] = useState({ w: 0, h: 0 });

  const [anchor, setAnchor] = useState<LogoAnchor>('br');
  const [scalePct, setScalePct] = useState(15);
  const [marginPx, setMarginPx] = useState(16);
  const [xPct, setXPct] = useState(70);
  const [yPct, setYPct] = useState(70);

  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [rendering, setRendering] = useState(false);
  const [loadingCore, setLoadingCore] = useState(false);
  const [outUrl, setOutUrl] = useState<string | null>(null);
  const [error, setError] = useState('');

  const g: any = typeof global !== 'undefined' ? global : {};
  const win = g.window || g;

  useEffect(() => {
    return () => {
      revokeUrl(videoUrl);
      revokeUrl(logoUrl);
      revokeUrl(outUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const measurePreview = useCallback(() => {
    const el = previewRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setPreviewSize({ w: rect.width, h: rect.height });
    }
  }, []);

  useEffect(() => {
    measurePreview();
    if (!win || !win.addEventListener) return;
    const onResize = () => measurePreview();
    win.addEventListener('resize', onResize);
    return () => win.removeEventListener('resize', onResize);
  }, [measurePreview, videoUrl, win]);

  const onPickVideo = (e: any) => {
    const file = e.target && e.target.files && e.target.files[0];
    if (!file) return;
    revokeUrl(videoUrl);
    revokeUrl(outUrl);
    setOutUrl(null);
    setVideoFile(file);
    const url = win.URL.createObjectURL(file);
    setVideoUrl(url);
    setVideoMeta({ w: 0, h: 0, dur: 0 });
    setError('');
  };

  const onPickLogo = (e: any) => {
    const file = e.target && e.target.files && e.target.files[0];
    if (!file) return;
    revokeUrl(logoUrl);
    revokeUrl(outUrl);
    setOutUrl(null);
    setLogoFile(file);
    const url = win.URL.createObjectURL(file);
    setLogoUrl(url);
    setError('');
    const img = new win.Image();
    img.onload = () => setLogoNat({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
    img.src = url;
  };

  const onVideoMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setVideoMeta({
      w: v.videoWidth || 0,
      h: v.videoHeight || 0,
      dur: v.duration || 0,
    });
    // Seek a bit for a frame preview
    try {
      if (v.currentTime < 0.1 && v.duration > 0.2) v.currentTime = 0.15;
    } catch {
      /* ignore */
    }
    measurePreview();
  };

  const sizeWarning = useMemo(() => {
    if (!videoFile) return '';
    const parts: string[] = [];
    if (videoFile.size > WARN_MB * 1024 * 1024) {
      parts.push(`File lớn (~${formatMb(videoFile.size)} MB). Gợi ý ≤ ${WARN_MB} MB.`);
    }
    if (videoMeta.dur > WARN_SEC) {
      parts.push(`Video dài (~${Math.round(videoMeta.dur)}s). Gợi ý ≤ ${WARN_SEC}s — file dài có thể làm treo tab.`);
    }
    return parts.join(' ');
  }, [videoFile, videoMeta.dur]);

  const box = useMemo(() => {
    const vw = previewSize.w || 1;
    const vh = previewSize.h || 1;
    return resolveLogoBox(anchor, scalePct, marginPx * (vw / Math.max(videoMeta.w || vw, 1)), xPct, yPct, vw, vh, logoNat.w, logoNat.h);
  }, [anchor, scalePct, marginPx, xPct, yPct, previewSize, videoMeta.w, logoNat]);

  const onLogoPointerDown = (e: any) => {
    if (anchor !== 'custom') setAnchor('custom');
    e.preventDefault && e.preventDefault();
    const clientX = e.clientX != null ? e.clientX : (e.touches && e.touches[0] && e.touches[0].clientX);
    const clientY = e.clientY != null ? e.clientY : (e.touches && e.touches[0] && e.touches[0].clientY);
    dragRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      origXPct: xPct,
      origYPct: yPct,
    };
  };

  useEffect(() => {
    if (!win || !win.addEventListener) return;
    const onMove = (e: any) => {
      if (!dragRef.current.active || !previewSize.w) return;
      const clientX = e.clientX != null ? e.clientX : (e.touches && e.touches[0] && e.touches[0].clientX);
      const clientY = e.clientY != null ? e.clientY : (e.touches && e.touches[0] && e.touches[0].clientY);
      if (clientX == null) return;
      const dx = ((clientX - dragRef.current.startX) / previewSize.w) * 100;
      const dy = ((clientY - dragRef.current.startY) / previewSize.h) * 100;
      setXPct(Math.max(0, Math.min(95, dragRef.current.origXPct + dx)));
      setYPct(Math.max(0, Math.min(95, dragRef.current.origYPct + dy)));
    };
    const onUp = () => {
      dragRef.current.active = false;
    };
    win.addEventListener('mousemove', onMove);
    win.addEventListener('mouseup', onUp);
    win.addEventListener('touchmove', onMove, { passive: false });
    win.addEventListener('touchend', onUp);
    return () => {
      win.removeEventListener('mousemove', onMove);
      win.removeEventListener('mouseup', onUp);
      win.removeEventListener('touchmove', onMove);
      win.removeEventListener('touchend', onUp);
    };
  }, [previewSize.w, previewSize.h, win]);

  const preloadCore = async () => {
    setLoadingCore(true);
    setError('');
    try {
      await ensureFFmpeg(setStatus);
    } catch (err: any) {
      setError((err && err.message) || 'Không tải được FFmpeg.');
    } finally {
      setLoadingCore(false);
    }
  };

  const cancelRender = () => {
    abortRef.current.aborted = true;
    setStatus('Đang hủy…');
  };

  const startRender = async () => {
    if (!videoFile || !logoFile || !videoMeta.w) {
      setError('Hãy chọn video và logo, đợi video load xong.');
      return;
    }
    abortRef.current = { aborted: false };
    setRendering(true);
    setProgress(0);
    setError('');
    revokeUrl(outUrl);
    setOutUrl(null);
    try {
      await ensureFFmpeg(setStatus);
      setStatus('Đang render overlay…');
      const blob = await renderLogoOverlay(videoFile, logoFile, {
        anchor,
        scalePct,
        marginPx,
        xPct,
        yPct,
        videoWidth: videoMeta.w,
        videoHeight: videoMeta.h,
        onProgress: (r) => setProgress(r),
        signal: abortRef.current,
      });
      if (abortRef.current.aborted) {
        setStatus('Đã hủy.');
        return;
      }
      const url = win.URL.createObjectURL(blob);
      setOutUrl(url);
      setProgress(1);
      setStatus('Xong — tải file MP4 bên dưới.');
    } catch (err: any) {
      if (abortRef.current.aborted) {
        setStatus('Đã hủy.');
      } else {
        setError((err && err.message) || 'Render thất bại.');
        setStatus('');
      }
    } finally {
      setRendering(false);
    }
  };

  const downloadOut = () => {
    if (!outUrl) return;
    const a = win.document.createElement('a');
    a.href = outUrl;
    a.download = (videoFile && videoFile.name ? videoFile.name.replace(/\.[^.]+$/, '') : 'video') + '-logo.mp4';
    win.document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <ToolLayout title="Chèn logo vào video">
      <p style={privacy}>
        Video và logo được xử lý hoàn toàn trên máy bạn (FFmpeg.wasm trong trình duyệt), không gửi lên server language123.net.
      </p>

      {!hasSharedArrayBuffer() ? (
        <p style={warnBox}>
          Trang chưa bật chế độ xử lý video (SharedArrayBuffer). Hãy mở trực tiếp{' '}
          <a href="/tools/video-logo">/tools/video-logo</a> (tải lại trang đầy đủ). Nếu vào từ menu Công cụ, link đã
          tự reload — nếu vẫn báo lỗi, thử Chrome/Edge mới nhất.
        </p>
      ) : null}

      <div style={card}>
        <label style={label}>1. Chọn video</label>
        <p style={hint}>MP4 / WebM / MOV · gợi ý clip ngắn ≤ 1–2 phút, ≤ ~{WARN_MB} MB.</p>
        <input type="file" accept="video/*" onChange={onPickVideo} />
        {videoUrl ? (
          <div style={{ ...previewWrap, marginTop: 12 }} ref={previewRef}>
            <video
              ref={videoRef}
              src={videoUrl}
              style={{ width: '100%', display: 'block', maxHeight: 420 }}
              controls
              muted
              playsInline
              onLoadedMetadata={onVideoMeta}
              onLoadedData={measurePreview}
            />
            {logoUrl && videoMeta.w > 0 ? (
              <img
                src={logoUrl}
                alt="logo overlay"
                draggable={false}
                onMouseDown={onLogoPointerDown}
                onTouchStart={onLogoPointerDown}
                style={{
                  ...logoOverlay,
                  left: box.left,
                  top: box.top,
                  width: box.w,
                  height: box.h,
                  objectFit: 'contain',
                }}
              />
            ) : null}
          </div>
        ) : null}
        {videoMeta.w > 0 ? (
          <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>
            {videoMeta.w}×{videoMeta.h}
            {videoMeta.dur > 0 ? ` · ${Math.round(videoMeta.dur)}s` : ''}
            {videoFile ? ` · ${formatMb(videoFile.size)} MB` : ''}
          </p>
        ) : null}
        {sizeWarning ? <p style={{ ...warnBox, marginTop: 12 }}>{sizeWarning}</p> : null}
      </div>

      <div style={card}>
        <label style={label}>2. Chọn logo (PNG / JPG / WebP, nền trong suốt tốt nhất)</label>
        <input type="file" accept="image/*" onChange={onPickLogo} />
      </div>

      <div style={card}>
        <label style={label}>3. Vị trí</label>
        <div style={btnRow}>
          {ANCHORS.map((a) => (
            <button
              key={a.id}
              type="button"
              style={{
                ...secondaryBtn,
                backgroundColor: anchor === a.id ? Colors.MAIN_COLOR : '#eceff1',
                color: anchor === a.id ? Colors.WHITE : Colors.TEXT,
              }}
              onClick={() => setAnchor(a.id)}
            >
              {a.label}
            </button>
          ))}
        </div>
        <label style={label}>Kích thước logo (% chiều rộng video): {scalePct}%</label>
        <input
          type="range"
          min={5}
          max={40}
          value={scalePct}
          onChange={(e: any) => setScalePct(Number(e.target.value))}
          style={{ width: '100%', marginBottom: 12 }}
        />
        <label style={label}>Lề (px, góc cố định): {marginPx}</label>
        <input
          type="range"
          min={0}
          max={80}
          value={marginPx}
          disabled={anchor === 'custom'}
          onChange={(e: any) => setMarginPx(Number(e.target.value))}
          style={{ width: '100%' }}
        />
        {anchor === 'custom' ? (
          <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>Kéo logo trên khung xem trước để đặt vị trí.</p>
        ) : null}
      </div>

      <div style={card}>
        <label style={label}>4. Render & tải về</label>
        <div style={btnRow}>
          <button type="button" style={secondaryBtn} disabled={loadingCore || rendering} onClick={preloadCore}>
            {loadingCore ? 'Đang tải FFmpeg…' : 'Tải FFmpeg trước (tuỳ chọn)'}
          </button>
          <button
            type="button"
            style={primaryBtn}
            disabled={!videoFile || !logoFile || rendering || !videoMeta.w}
            onClick={startRender}
          >
            {rendering ? `Đang render… ${Math.round(progress * 100)}%` : 'Render MP4'}
          </button>
          {rendering ? (
            <button type="button" style={dangerBtn} onClick={cancelRender}>
              Hủy
            </button>
          ) : null}
          {outUrl ? (
            <button type="button" style={primaryBtn} onClick={downloadOut}>
              Tải MP4
            </button>
          ) : null}
        </div>
        {rendering || progress > 0 ? (
          <div style={{ height: 8, background: '#eceff1', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div
              style={{
                width: `${Math.round(progress * 100)}%`,
                height: '100%',
                background: Colors.MAIN_COLOR,
                transition: 'width 0.2s',
              }}
            />
          </div>
        ) : null}
        {status ? <p style={hint}>{status}</p> : null}
        {error ? <p style={{ ...warnBox, color: '#b71c1c', background: '#ffebee' }}>{error}</p> : null}
        {outUrl ? (
          <div style={{ marginTop: 12 }}>
            <video src={outUrl} controls style={{ width: '100%', borderRadius: 8, background: '#000' }} />
          </div>
        ) : null}
      </div>
    </ToolLayout>
  );
};

export default VideoLogoOverlay;
